import { FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { Button } from "@/components/Button/Button";
import { Modal } from "@/components";
import { useSearch } from "@/utils";
import { User } from "@/pages/Public/pages/Login/schema/login";

import {
	useGetEmailsQuery,
	useReconnectEmailMutation,
} from "@/pages/Private/redux/email/email.api";
import { Table, TableColumn } from "@/components/Table/Table";

import { useCreateSecondaryEmailUnipileSessionMutation } from "@/pages/Private/redux/unipile/unipile.api";

import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { Email, isEmailLoggedOut } from "../../Account/schema/email";
import { EmailPanel } from "../../Account/components/EmailPanel";

interface EmailsTableProps {
	user: User | null;
	isVisible: boolean;
	handleClose: () => void;
	companyId?: number;
	slotData?: {
		emailsAmount: number;
		emailsUsed: number;
	};
}

export const EmailsTable: FunctionComponent<EmailsTableProps> = ({
	user,
	isVisible,
	handleClose,
	slotData,
}) => {
	const { t } = useTranslation();
	const [showEmailSettingsModal, setShowEmailSettingsModal] = useState<boolean>(false);
	const [createUnipileSession, { isLoading: isCreateUnipileSessionLoading }] =
		useCreateSecondaryEmailUnipileSessionMutation();
	const [email, setEmail] = useState<Email | null>(null);

	const [reconnectEmail, { isLoading: isReconnectEmailLoading }] = useReconnectEmailMutation();

	const { searchValue, page, limit } = useSearch();
	const [error, setError] = useState<string | null>(null);

	const { data: emails, isLoading } = useGetEmailsQuery({
		userId: user?.id ?? 0,
		page,
		limit,
		searchValue,
	});

	const handleReconnectEmail = async (emailId: number) => {
		try {
			const response = await reconnectEmail({
				id: emailId,
				successRedirectUrl: window.location.href || undefined,
			}).unwrap();

			if (response.url) {
				window.location.href = response.url;
			}
		} catch (error) {
			console.error(error);
		}
	};

	const columns: TableColumn[] = useMemo(
		() => [
			{
				title: "Email",
				uid: "email",
				render: (row) => <div className="flex items-start justify-start">{row?.email}</div>,
			},
			{
				title: "Sender Name",
				uid: "senderName",
				render: (row: Email) => (
					<div className="flex items-start justify-start">
						{row?.senderFirstName} {row?.senderLastName}
					</div>
				),
			},
			{
				title: "Provider",
				uid: "provider",
				render: (row: Email) => (
					<div className="flex items-start justify-start">
						{t(`email.providers.${row?.provider}`)}
					</div>
				),
			},
			{
				title: "Type",
				uid: "type",
				render: (row: Email) => (
					<div className="flex items-start justify-start">{row?.type.capitalizeString()}</div>
				),
			},
			{
				title: "Limit per day",
				uid: "limitPerDay",
				render: (row: Email) => (
					<div className="flex items-start justify-start">{row?.limitPerDay}</div>
				),
			},
			{
				title: "Status",
				uid: "status",
				render: (row: Email) => (
					<div className="flex items-start justify-start">
						{isEmailLoggedOut(row?.status) ? "Logged out" : "Connected"}
						{isEmailLoggedOut(row?.status) && (
							<div className="w-fit ml-2">
								<Button
									color={ButtonColor.ACTION}
									isLoading={isReconnectEmailLoading}
									size={ButtonSize.XS}
									title="Reconnect Email"
									onClick={(e) => {
										e.stopPropagation();
										handleReconnectEmail(row.id);
									}}
								/>
							</div>
						)}
					</div>
				),
			},
		],
		[]
	);

	const handleCreateUnipileSession = async () => {
		try {
			const response = await createUnipileSession({
				successRedirectUrl: window.location.href,
				userId: user?.id,
			}).unwrap();

			if (response.url) {
				window.location.href = response.url;
			}
		} catch (error) {
			setError(((error as FetchBaseQueryError).data as { message: string }).message);
		}
	};

	return (
		<Modal
			disableSave={(slotData?.emailsUsed || 0) >= (slotData?.emailsAmount || 0)}
			handleClose={handleClose}
			handleSave={() => {
				handleCreateUnipileSession();
			}}
			isLoading={isLoading}
			isOpened={isVisible}
			size="lg"
			submitButtonText="Add Email"
			title={"Emails"}
		>
			<>
				<div className="flex flex-col"></div>
				<Table
					columns={columns}
					data={emails?.data ?? []}
					handleRowClick={(id: number) => {
						const email = emails?.data?.find((item) => item.id === id);

						if (email) {
							setShowEmailSettingsModal(true);
							setEmail(email);
						}
					}}
					hideFooter={true}
					hidePagination={!emails?.data || emails?.data?.length === 0}
					showFooterFunctions={false}
				/>
				{error && <div className="text-text-meta text-sm">{error}</div>}
			</>
			{showEmailSettingsModal && email && (
				<EmailPanel
					allowDelete={true}
					email={email}
					handleClose={() => setShowEmailSettingsModal(false)}
				/>
			)}
		</Modal>
	);
};
